
import React from 'react';
import { useCompanyPerks } from '@/hooks/useCompanyPerks';
import { Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

interface CompanyPerksProps {
  companyId: string;
  companyName: string;
  showAsInline?: boolean;
}

export const CompanyPerks: React.FC<CompanyPerksProps> = ({ 
  companyId, 
  companyName,
  showAsInline = false 
}) => {
  const { perks, loading, hasPerks } = useCompanyPerks(companyId);
  
  const perksArray = perks ? [
    perks.perk1,
    perks.perk2, 
    perks.perk3,
    perks.perk4,
    perks.perk5
  ].filter(Boolean) : [];

  if (loading) {
    return showAsInline ? null : (
      <div className="mt-4">
        <Skeleton className="h-10 w-32 mb-2" />
      </div>
    );
  }

  if (!hasPerks) {
    return null;
  }

  // Only render the button if showAsInline is true
  if (!showAsInline) {
    return null;
  }

  const buttonContent = (
    <Button 
      variant="outline" 
      className="flex items-center gap-2 border-redflags-red text-redflags-red hover:bg-redflags-red/10 w-full"
    >
      <Sparkles className="h-4 w-4" />
      View Company Perks
    </Button>
  );

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="mt-4">
          {buttonContent}
        </div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-redflags-red" />
            {companyName} Perks
          </DialogTitle>
          <DialogDescription>
            Employee benefits and perks offered by {companyName}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          <ul className="space-y-3">
            {perksArray.map((perk, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyPerks;
